import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Mail } from "lucide-react";
import { API_BASE } from "@/lib/api";

/**
 * Public magic-link landing page. NOT gated by auth — the token IS the auth.
 * Backend endpoint: GET /api/public/email/verify?token=…
 */
export default function VerifySender() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, ok: null, reason: null, email: null });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, reason: "missing_token", email: null });
      return;
    }
    fetch(`${API_BASE}/public/email/verify?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => setState({ loading: false, ok: !!d.ok, reason: d.reason, email: d.email }))
      .catch(() => setState({ loading: false, ok: false, reason: "network", email: null }));
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center" data-testid="verify-loading">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#8E9196]">Verifying…</div>
      </div>
    );
  }

  if (state.ok) {
    return (
      <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center p-8" data-testid="verify-success">
        <div className="max-w-md text-center">
          <ShieldCheck className="w-14 h-14 text-[#00F0FF] mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-2">Sender verified</div>
          <div className="font-display font-black text-3xl mb-3">You&apos;re confirmed</div>
          <p className="text-sm text-[#8E9196] mb-2">
            <span className="font-mono text-white">{state.email}</span> is now trusted by the SOC.
          </p>
          <p className="text-sm text-[#8E9196]">
            Just email your normal case-triggering address any time — ForensiQ will automatically open an investigation and reply with the report.
          </p>
        </div>
      </div>
    );
  }

  const isExpired = state.reason === "expired";
  return (
    <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center p-8" data-testid="verify-failure">
      <div className="max-w-md text-center">
        {isExpired
          ? <Mail className="w-14 h-14 text-[#FFC542] mx-auto mb-4" strokeWidth={1.5} />
          : <ShieldAlert className="w-14 h-14 text-[#FF2E63] mx-auto mb-4" strokeWidth={1.5} />}
        <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: isExpired ? "#FFC542" : "#FF2E63" }}>
          {isExpired ? "Link expired" : "Verification failed"}
        </div>
        <div className="font-display font-black text-3xl mb-3">
          {isExpired ? "Ask for a fresh link" : "This link isn't valid"}
        </div>
        <p className="text-sm text-[#8E9196] mb-4">
          {isExpired
            ? <>The magic link for <span className="font-mono text-white">{state.email}</span> has expired. Just email the SOC again — a new link will arrive within a minute.</>
            : <>The token is missing, malformed, or was already used. Contact the SOC team if you believe this is a mistake.</>}
        </p>
        <Link to="/" className="inline-block text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">← Back to ForensiQ</Link>
      </div>
    </div>
  );
}
